import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, FolderOpen, TrendingUp, Activity, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { adminAPI } from '../../lib/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#E8A095', '#8DA399', '#E8C495', '#95B8E8', '#D68282', '#4A2C2A', '#A89B9A'];

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const healthConcernsData = stats?.health_concerns_distribution 
    ? Object.entries(stats.health_concerns_distribution).map(([name, value]) => ({
        name: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        value
      }))
    : [];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        <div>
          <h1 className="font-heading text-3xl text-[#4A2C2A]">Admin Dashboard</h1>
          <p className="text-[#4A2C2A]/60">Overview of your Rejuven platform</p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="soft-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#E8A095]" />
              </div>
              <span className="text-sm text-[#4A2C2A]/60">Total Users</span>
            </div>
            <p className="text-3xl font-heading text-[#4A2C2A]">{stats?.total_users || 0}</p>
            <p className="text-sm text-[#8DA399] mt-1">
              {stats?.active_users || 0} active
            </p>
          </div>

          <div className="soft-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#8DA399]/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#8DA399]" />
              </div>
              <span className="text-sm text-[#4A2C2A]/60">Total Reports</span>
            </div>
            <p className="text-3xl font-heading text-[#4A2C2A]">{stats?.total_reports || 0}</p>
          </div>

          <div className="soft-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#E8C495]/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-[#E8C495]" />
              </div>
              <span className="text-sm text-[#4A2C2A]/60">Check-ins</span>
            </div>
            <p className="text-3xl font-heading text-[#4A2C2A]">{stats?.total_checkins || 0}</p>
          </div>

          <div className="soft-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#95B8E8]/20 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-[#95B8E8]" />
              </div>
              <span className="text-sm text-[#4A2C2A]/60">Documents</span>
            </div>
            <p className="text-3xl font-heading text-[#4A2C2A]">{stats?.total_documents || 0}</p>
          </div>
        </div>

        {/* Charts and Quick Actions */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Health Concerns Distribution */}
          <div className="soft-card p-6">
            <h2 className="font-heading text-xl text-[#4A2C2A] mb-6">Health Concerns Distribution</h2>
            {healthConcernsData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={healthConcernsData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {healthConcernsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-[#4A2C2A]/50">
                No data available
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-4">
              {healthConcernsData.map((item, index) => (
                <div key={item.name} className="flex items-center gap-2 text-sm">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-[#4A2C2A]/70">{item.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="soft-card p-6">
            <h2 className="font-heading text-xl text-[#4A2C2A] mb-6">Quick Actions</h2>
            <div className="space-y-3">
              <Link 
                to="/admin/users"
                className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl hover:bg-[#E8A095]/10 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#E8A095]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#4A2C2A]">Manage Users</p>
                    <p className="text-sm text-[#4A2C2A]/50">View, edit, and manage users</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#4A2C2A]/30" />
              </Link>

              <Link 
                to="/admin/documents"
                className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl hover:bg-[#E8A095]/10 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#8DA399]/20 flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-[#8DA399]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#4A2C2A]">Upload Documents</p>
                    <p className="text-sm text-[#4A2C2A]/50">Add wellness knowledge base</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#4A2C2A]/30" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
